import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { messageStrings } from '../../../constants/strings';
import { Icon } from '../../Icon';

type Props = {
  timetoken: string;
  onShowHistory: (timetoken: string) => void;
};

export function MessageAiHistoryMenu({ timetoken, onShowHistory }: Props) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({ top: rect.bottom + 4, left: rect.left });
  }, []);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        anchorRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    updatePosition();
    setOpen(true);
  };

  const handleHistoryClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    onShowHistory(timetoken);
  };

  const menu = open ? (
    <div
      ref={menuRef}
      className="fixed z-[9999] min-w-[10.5rem] overflow-hidden rounded-md border border-neutral-200 bg-white py-1 shadow-md"
      style={{ top: position.top, left: position.left }}
      role="menu"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-neutral-50"
        onClick={handleHistoryClick}
      >
        <Icon
          icon="aiStar"
          width={16}
          height={16}
          className="shrink-0 text-blue-500"
        />
        <span>{messageStrings.aiHistoryTitle}</span>
      </button>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={handleToggle}
        className="relative flex h-6 w-6 shrink-0 items-center justify-center self-center rounded transition-colors hover:bg-neutral-100"
        aria-label={messageStrings.aiMessageMenuLabel}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Icon
          icon="moreVertical"
          width={16}
          height={16}
          className="text-neutral-400"
        />
      </button>

      {typeof document !== 'undefined' && menu
        ? createPortal(menu, document.body)
        : null}
    </>
  );
}
